import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import CloseIcon from "@mui/icons-material/Close";
// Brand logo upload is paused for now (see the commented blocks below).
// import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { AuthAPI } from "../../api/authAPI";
import AuthLayout from "../../components/AuthLayout";
import AuthProgressBar from "../../components/AuthProgressBar";
import { useOnboarding } from "../../context/OnboardingContext";
import { useAuth } from "../../context/AuthContext";
import { shouldRouteToRegisterStatusAfterSignup } from "../../utils/onboardingRouting";
import { BRAND_CATEGORIES } from "../../utils/constants";
import {
  MarketplaceBrandNameForm,
  MarketplaceBrandNameRow,
  MarketplaceStatusChip,
  marketplaceLabel,
} from "../../components/MarketplaceBrandNames";

const MARKETPLACE_OPTIONS = ["Amazon", "Flipkart", "Myntra", "Nykaa", "Tira"];
const GMV_OPTIONS = [
  "< ₹10 Lakh",
  "₹10 Lakh - ₹50 Lakh",
  "₹50 Lakh - ₹1 Cr",
  "₹1 Cr - ₹10 Cr",
  "₹10 Cr - ₹100 Cr",
  "> ₹100 Cr",
];
const INFLUENCER_OPTIONS = ["< 10", "10-50", "50 - 100", "100 - 500", "> 500"];
const CHECKBOX_PILL_SX = {
  border: "1px solid #e5e5e5",
  borderRadius: "8px",
  mx: 0,
  gap: "4px",
  py: "6px",
  pr: "12px",
  pl: "6px",
  minHeight: 32,
  alignItems: "center",
  ".MuiFormControlLabel-label": {
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: 500,
  },
};

function PillSelector({ options, value, onChange }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {options.map((opt) => {
        const selected = value === opt;
        return (
          <Chip
            key={opt}
            label={opt}
            onClick={() => onChange(opt)}
            sx={{
              borderRadius: "8px",
              backgroundColor: selected ? "#ee4d37" : "#fff",
              color: selected ? "#fff" : "#808080",
              border: selected ? "1px solid #ee4d37" : "1px solid #e5e5e5",
              fontWeight: 500,
              fontSize: 14,
            }}
          />
        );
      })}
    </Box>
  );
}

export default function OnboardingStep2() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { email, step1Data } = useOnboarding();
  const { setEmailUserAuthToken, setAdminAuthToken, logout } = useAuth();

  // const [brandLogo, setBrandLogo] = useState(null);
  // const [brandLogoName, setBrandLogoName] = useState("");
  const [brandCategory, setBrandCategory] = useState("");
  const [marketplaces, setMarketplaces] = useState([]);
  const [marketplaceBrandNames, setMarketplaceBrandNames] = useState([]);
  const [gmvRange, setGmvRange] = useState("");
  const [influencerCount, setInfluencerCount] = useState("");
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!step1Data || !email) {
      navigate("/register", { replace: true });
    }
  }, [email, step1Data, navigate]);

  const signedInEmail = useMemo(() => email || "", [email]);
  const brandName = step1Data?.brandName || "";
  const brandWebsite = step1Data?.brandWebsite || "";

  const validateBeforeSubmit = () => {
    const nextErrors = {};
    // if (!brandLogo) {
    //   nextErrors.brandLogo = "Brand logo is required";
    // }
    if (!brandCategory) {
      nextErrors.brandCategory = "Please select a category";
    }
    if (!gmvRange) {
      nextErrors.gmvRange = "Please select gross monthly sales";
    }
    if (!influencerCount) {
      nextErrors.influencerCount = "Please select number of influencers";
    }

    setErrors((prev) => ({ ...prev, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  /*
  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        brandLogo: "Please upload a valid image file.",
      }));
      return;
    }

    const maxSizeInBytes = 2 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      setErrors((prev) => ({
        ...prev,
        brandLogo: "Logo should be smaller than 2 MB.",
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setBrandLogo(reader.result);
      setBrandLogoName(file.name);
      setErrors((prev) => ({ ...prev, brandLogo: "" }));
    };
    reader.readAsDataURL(file);
  };
  */

  const onSubmit = async () => {
    if (!validateBeforeSubmit()) {
      return;
    }

    setSubmitLoading(true);
    try {
      const submitData = {
        firstName: step1Data.firstName,
        lastName: step1Data.lastName || "",
        workEmail: signedInEmail,
        contactNumber: `${step1Data.countryCode || "+91"}${step1Data.contactNumber || ""}`,
        designation: step1Data.designation || "",
        designationOther: step1Data.designationOther || "",
        hearAboutUs: step1Data.hearAboutUs || "",
        hearAboutUsOther: step1Data.hearAboutUsOther || "",
        brandName,
        brandWebsite,
        brandInstagram: "",
        brandCategory,
        // brandLogo, // logo upload paused; the backend field is optional
        gmvRange,
        aov: "",
        influencerCount,
        interest: [],
        marketplaces,
        marketplace_brand_names: marketplaceBrandNames,
      };

      const response = await AuthAPI.createOnboardingUser(submitData);
      const data = response?.data || {};

      await queryClient.cancelQueries({ queryKey: ["currentUser"] });
      queryClient.removeQueries({ queryKey: ["currentUser"] });

      [localStorage, sessionStorage].forEach((storage) => {
        storage.removeItem("Token");
        storage.removeItem("authToken");
        storage.removeItem("user_type");
      });

      localStorage.setItem("authToken", data.token);
      localStorage.setItem("user_type", data.user_type || "emailUser");
      setEmailUserAuthToken(data.token);
      setAdminAuthToken(null);

      await queryClient.prefetchQuery({
        queryKey: ["currentUser"],
        queryFn: () => AuthAPI.getCurrentUser(),
      });

      if (shouldRouteToRegisterStatusAfterSignup(data.registration_status)) {
        navigate("/register/status", {
          replace: true,
          state: { justSubmitted: true },
        });
        return;
      }
      navigate("/dashboard/home", { replace: true });
    } catch (error) {
      const payload = error?.response?.data || {};
      enqueueSnackbar(
        payload.message || "Something went wrong. Please try again.",
        { variant: "error" },
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const addMarketplaceBrandName = ({ platformId, name }) => {
    const isDuplicate = marketplaceBrandNames.some(
      (row) =>
        row.platform_id === platformId &&
        row.name.toLowerCase() === name.toLowerCase(),
    );
    if (!isDuplicate) {
      setMarketplaceBrandNames((prev) => [
        ...prev,
        { platform_id: platformId, name },
      ]);
    }
    return !isDuplicate;
  };

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <AuthLayout>
      <Box>
        <AuthProgressBar step={2} />
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography
            sx={{ fontSize: 24, fontWeight: 600, lineHeight: "32px" }}
          >
            Brand Details
          </Typography>

          {/* Brand logo upload is paused for now.
          <Stack spacing={1}>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
              Upload Brand Logo
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                component="label"
                startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 18 }} />}
                sx={{
                  backgroundColor: "#fce2dd",
                  color: "#ee4d37",
                  borderRadius: "8px",
                  textTransform: "none",
                  minWidth: "auto",
                  px: 1,
                  py: "6px",
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: "20px",
                  "&:hover": { backgroundColor: "#f8d5ce" },
                }}
              >
                Upload
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleLogoUpload}
                />
              </Button>
              {brandLogo ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={brandLogo}
                    alt="Brand logo preview"
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      objectFit: "cover",
                      border: "1px solid #e5e5e5",
                    }}
                  />
                  <Typography
                    sx={{
                      color: "#808080",
                      fontSize: 12,
                      maxWidth: 220,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {brandLogoName}
                  </Typography>
                  <Button
                    onClick={() => {
                      setBrandLogo(null);
                      setBrandLogoName("");
                    }}
                    sx={{
                      color: "#808080",
                      minWidth: "auto",
                      p: "2px",
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </Button>
                </Box>
              ) : null}
            </Box>
            {errors.brandLogo ? (
              <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: 0.5 }}>
                {errors.brandLogo}
              </Typography>
            ) : null}
          </Stack>
          */}

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
              Brand Category
            </Typography>
            <FormControl size="small" error={Boolean(errors.brandCategory)}>
              <Select
                displayEmpty
                data-cy="brand-category-select"
                value={brandCategory}
                onChange={(e) => {
                  setBrandCategory(e.target.value);
                  if (errors.brandCategory)
                    setErrors((prev) => ({ ...prev, brandCategory: "" }));
                }}
                renderValue={(selected) => selected || "Select brand category"}
                sx={{
                  height: 40,
                  fontSize: 14,
                  "& .MuiSelect-select": {
                    color: brandCategory ? "inherit" : "#808080",
                    fontWeight: brandCategory ? 500 : 400,
                  },
                }}
              >
                {BRAND_CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    <Typography>{cat}</Typography>
                  </MenuItem>
                ))}
              </Select>
              {errors.brandCategory ? (
                <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: 0.5 }}>
                  {errors.brandCategory}
                </Typography>
              ) : null}
            </FormControl>
          </Stack>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
              Gross Monthly Sales
            </Typography>
            <PillSelector
              options={GMV_OPTIONS}
              value={gmvRange}
              onChange={(value) => {
                setGmvRange(value);
                if (errors.gmvRange) {
                  setErrors((prev) => ({ ...prev, gmvRange: "" }));
                }
              }}
            />
            {errors.gmvRange ? (
              <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: 0.5 }}>
                {errors.gmvRange}
              </Typography>
            ) : null}
          </Stack>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
              Number of influencers you work with
            </Typography>
            <PillSelector
              options={INFLUENCER_OPTIONS}
              value={influencerCount}
              onChange={(value) => {
                setInfluencerCount(value);
                if (errors.influencerCount) {
                  setErrors((prev) => ({ ...prev, influencerCount: "" }));
                }
              }}
            />
            {errors.influencerCount ? (
              <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: 0.5 }}>
                {errors.influencerCount}
              </Typography>
            ) : null}
          </Stack>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
              Select marketplaces where your products are listed
            </Typography>
            <FormGroup row sx={{ gap: 1, flexWrap: "nowrap" }}>
              {MARKETPLACE_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt}
                  sx={{
                    ...CHECKBOX_PILL_SX,
                    backgroundColor: marketplaces.includes(opt)
                      ? "#ee4d37"
                      : "#fff",
                    color: marketplaces.includes(opt) ? "#fff" : "#808080",
                  }}
                  control={
                    <Checkbox
                      checked={marketplaces.includes(opt)}
                      onChange={() =>
                        setMarketplaces((prev) =>
                          prev.includes(opt)
                            ? prev.filter((item) => item !== opt)
                            : [...prev, opt],
                        )
                      }
                      sx={{
                        p: "1px",
                        mr: "4px",
                        ".MuiSvgIcon-root": { fontSize: 18 },
                        color: marketplaces.includes(opt) ? "#fff" : "#808080",
                        "&.Mui-checked": { color: "#fff" },
                      }}
                    />
                  }
                  label={opt}
                />
              ))}
            </FormGroup>
          </Stack>

          <Stack spacing={1}>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
              Brand Name on marketplaces
            </Typography>
            <MarketplaceBrandNameForm onAdd={addMarketplaceBrandName} />
            {marketplaceBrandNames.length ? (
              <>
                <Typography sx={{ fontSize: 12, color: "#808080", pt: 1 }}>
                  Your Brand names
                </Typography>
                {marketplaceBrandNames.map((row) => (
                  <MarketplaceBrandNameRow
                    key={`${row.platform_id}-${row.name}`}
                    name={row.name}
                    platformLabel={marketplaceLabel(row.platform_id)}
                  >
                    <MarketplaceStatusChip status="pending_review" />
                    <Button
                      aria-label={`Remove ${row.name}`}
                      onClick={() =>
                        setMarketplaceBrandNames((prev) =>
                          prev.filter((item) => item !== row),
                        )
                      }
                      sx={{ color: "#973236", minWidth: "auto", p: "2px" }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </Button>
                  </MarketplaceBrandNameRow>
                ))}
              </>
            ) : null}
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => navigate("/register", { replace: true })}
              sx={{
                backgroundColor: "#fce2dd",
                color: "#ee4d37",
                boxShadow: "none",
                borderRadius: "8px",
                height: 48,
                "&:hover": { backgroundColor: "#f8d5ce", boxShadow: "none" },
              }}
            >
              ← Previous
            </Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={submitLoading}
              sx={{
                backgroundColor: "#ee4d37",
                borderRadius: "8px",
                height: 48,
                "&:hover": { backgroundColor: "#d94430" },
              }}
            >
              {submitLoading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Submit →"
              )}
            </Button>
          </Box>

          <Box
            sx={{
              mt: 1,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: "#808080",
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: 14, lineHeight: "20px" }}
            >
              You are signed in as
            </Typography>
            <Typography
              component="span"
              sx={{
                color: "#0d0d0d",
                fontWeight: 600,
                fontSize: 14,
                lineHeight: "20px",
              }}
            >
              {signedInEmail}
            </Typography>
            <Typography
              component="span"
              sx={{ color: "#d1d1d1", fontSize: 12 }}
            >
              |
            </Typography>
            <Button
              onClick={onLogout}
              sx={{
                minWidth: "auto",
                p: 0,
                color: "#ee4d37",
                textTransform: "none",
                fontSize: 14,
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              Log Out
            </Button>
          </Box>
        </Stack>
      </Box>
    </AuthLayout>
  );
}
